import React, { useEffect, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';

interface EmailViewerProps {
  htmlContent: string;
  subject: string;
  recipientEmail: string;
  className?: string;
}

/**
 * Componente para visualização de emails HTML como o destinatário recebeu
 */
const EmailViewer: React.FC<EmailViewerProps> = ({
  htmlContent,
  subject,
  recipientEmail,
  className = '',
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(600);

  // CSS clean e moderno para emails
  const emailCSS = `
    <style>
      * {
        box-sizing: border-box;
      }
      
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        line-height: 1.6;
        color: #1f2937;
        margin: 0;
        padding: 32px;
        background-color: #ffffff;
      }
      
      .email-content {
        max-width: 600px;
        margin: 0 auto;
      }
      
      /* Typography */
      .email-content h1 {
        color: #0f172a;
        font-size: 28px;
        font-weight: 700;
        margin: 0 0 24px 0;
        line-height: 1.3;
      }
      
      .email-content h2 {
        color: #1e293b;
        font-size: 22px;
        font-weight: 600;
        margin: 32px 0 16px 0;
        line-height: 1.4;
      }
      
      .email-content h3 {
        color: #334155;
        font-size: 18px;
        font-weight: 600;
        margin: 24px 0 12px 0;
        line-height: 1.4;
      }
      
      .email-content p {
        margin: 0 0 20px 0;
        color: #475569;
        font-size: 16px;
        line-height: 1.7;
      }
      
      .email-content p:last-child {
        margin-bottom: 0;
      }
      
      /* Links */
      .email-content a {
        color: #3b82f6;
        text-decoration: none;
        font-weight: 500;
        transition: color 0.2s ease;
      }
      
      .email-content a:hover {
        color: #1d4ed8;
        text-decoration: underline;
      }
      
      /* Buttons */
      .email-content .button,
      .email-content .btn,
      .email-content a[style*="background"] {
        display: inline-block;
        padding: 14px 28px;
        background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
        color: #ffffff !important;
        text-decoration: none;
        border-radius: 8px;
        font-weight: 600;
        font-size: 16px;
        margin: 16px 0;
        transition: all 0.2s ease;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
      }
      
      .email-content .button:hover,
      .email-content .btn:hover {
        background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        text-decoration: none;
      }
      
      /* Tables */
      .email-content table {
        width: 100%;
        border-collapse: collapse;
        margin: 24px 0;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
      
      .email-content table td,
      .email-content table th {
        padding: 12px 16px;
        text-align: left;
        border-bottom: 1px solid #e2e8f0;
      }
      
      .email-content table th {
        background-color: #f8fafc;
        font-weight: 600;
        color: #374151;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      
      .email-content table tr:last-child td {
        border-bottom: none;
      }
      
      .email-content table tr:hover {
        background-color: #f9fafb;
      }
      
      /* Images */
      .email-content img {
        max-width: 100%;
        height: auto;
        border-radius: 8px;
        margin: 16px 0;
      }
      
      /* Lists */
      .email-content ul,
      .email-content ol {
        margin: 20px 0;
        padding-left: 24px;
      }
      
      .email-content li {
        margin-bottom: 8px;
        color: #475569;
        line-height: 1.6;
      }
      
      /* Blockquotes */
      .email-content blockquote {
        border-left: 4px solid #3b82f6;
        padding: 16px 20px;
        margin: 24px 0;
        background-color: #f8fafc;
        border-radius: 0 8px 8px 0;
        font-style: italic;
        color: #64748b;
      }
      
      /* Code */
      .email-content code {
        background-color: #f1f5f9;
        color: #e11d48;
        padding: 3px 6px;
        border-radius: 4px;
        font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
        font-size: 14px;
        font-weight: 500;
      }
      
      .email-content pre {
        background-color: #0f172a;
        color: #e2e8f0;
        padding: 20px;
        border-radius: 8px;
        overflow-x: auto;
        margin: 24px 0;
        font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
        font-size: 14px;
        line-height: 1.5;
      }
      
      .email-content pre code {
        background: none;
        color: inherit;
        padding: 0;
      }
      
      /* Dividers */
      .email-content hr {
        border: none;
        height: 1px;
        background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
        margin: 32px 0;
      }
      
      /* Responsive */
      @media (max-width: 640px) {
        body {
          padding: 16px;
        }
        
        .email-content h1 {
          font-size: 24px;
        }
        
        .email-content h2 {
          font-size: 20px;
        }
        
        .email-content .button,
        .email-content .btn {
          display: block;
          text-align: center;
          margin: 16px 0;
        }
      }
    </style>
  `;

  // Monta o HTML completo do email
  const fullEmailHTML = `
    <!DOCTYPE html>
    <html lang="pt-BR">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${subject}</title>
      ${emailCSS}
    </head>
    <body>
      <div class="email-content">
        ${htmlContent}
      </div>
    </body>
    </html>
  `;

  // Ajusta a altura do iframe automaticamente baseado no conteúdo
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const adjustHeight = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDoc) {
          // Aguarda o conteúdo carregar completamente
          setTimeout(() => {
            const body = iframeDoc.body;
            const html = iframeDoc.documentElement;
            
            if (body && html) {
              // Remove qualquer scroll do iframe
              body.style.overflow = 'visible';
              html.style.overflow = 'visible';
              
              // Calcula a altura total do conteúdo
              const height = Math.max(
                body.scrollHeight,
                body.offsetHeight,
                html.clientHeight,
                html.scrollHeight,
                html.offsetHeight
              );
              
              // Define uma altura mínima, mas sem máxima para permitir emails longos
              const minHeight = 400;
              const finalHeight = Math.max(height + 40, minHeight); // +40px para margem extra
              
              setIframeHeight(finalHeight);
            }
          }, 200);
        }
      } catch (error) {
        // Em caso de erro (cross-origin), usa altura padrão
        console.warn('Não foi possível ajustar altura automaticamente:', error);
        setIframeHeight(600);
      }
    };

    // Ajusta altura quando o iframe carrega
    iframe.onload = adjustHeight;
    
    // Ajusta altura imediatamente se já carregou
    if (iframe.contentDocument?.readyState === 'complete') {
      adjustHeight();
    }

    return () => {
      if (iframe) {
        iframe.onload = null;
      }
    };
  }, [htmlContent]);

  return (
    <div className={`email-viewer-container ${className}`}>
      <iframe
        ref={iframeRef}
        srcDoc={fullEmailHTML}
        title={`Email Preview: ${subject}`}
        className="w-full border-0 rounded-lg"
        style={{ 
          height: `${iframeHeight}px`,
          overflow: 'hidden'
        }}
        sandbox="allow-same-origin"
        scrolling="no"
      />
    </div>
  );
};

export default EmailViewer;
